import { Component } from '@angular/core';
import { BaseComponent } from 'src/app/components/base.component';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { DocIssueType, DocIssueTypeExtension } from '@models/doc-issue-type.enum';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { TabStates } from '@utilities/tab-states';
import { FolderSdk } from '@sdks/folder.sdk';
import { map, Observable, takeUntil, } from 'rxjs';
import { NotificationManager } from '@managers/notification.manager';
import { DocViewerParamReader } from '@utilities/doc-viewer-param-reader';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DocViewerConnectionsDialogComponent } from '../../dialogs/doc-viewer-connections-dialog/doc-viewer-connections-dialog.component';
import { ConnectionsDialogModel } from '../../dialogs/doc-viewer-connections-dialog/connections-dialog.model';
import { DocViewerHistoryDialogComponent } from '../../dialogs/doc-viewer-history-dialog/doc-viewer-history-dialog.component';
import { MultiDocEditDialogModel } from '@dialogs/multi-doc-edit-dialog/multi-doc-edit-dialog.model';
import { BuiltInFieldId } from '@models/built-in-field-id.enum';
import { FieldType } from '@models/field/field-type.enum';
import { MultiDocPostBody } from '@models/review/multi-doc-post-body';
import { LexbeDoc } from '@models/doc.model';
import { MultiDocEditComponent } from '../../dialogs/multi-doc-edit/multi-doc-edit.component';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { EmailEditComponent } from '../../dialogs/email-edit/email-edit.component';
import { EmailEditDialogModel } from '../../dialogs/email-edit/email-edit-dialog.model';
import { UserConfigsService } from '@services/user-configs.service';

@Component({
    selector: 'lex-doc-viewer-doc-tab',
    templateUrl: './doc-viewer-doc-tab.component.html',
    styleUrls: ['./doc-viewer-doc-tab.component.scss'],
    standalone: false
})
export class DocViewerDocTabComponent extends BaseComponent {

	public currentDocument$ = this.docViewerStore.currentDocument$;
	public hasEmailMetadata$ = this.currentDocument$.pipe(
		map((currentDocSnapshot) => {
			return !!(currentDocSnapshot.email.fromField
				|| currentDocSnapshot.email.toField
				|| currentDocSnapshot.email.cc
				|| currentDocSnapshot.email.bcc
				|| currentDocSnapshot.email.subjectMatter);
		})
	);
	public hasPlaceHolderText$ = this.currentDocument$.pipe(
		map((currentDocumentSnapshot) => {
			return (currentDocumentSnapshot.status.issues & DocIssueType.IsPlaceholder) !== 0;
		})
	);
	public placeHolderText$ = this.currentDocument$.pipe(
		map((currentDocumentSnapshot) => {
			return DocIssueTypeExtension.getIssueDisplayString(currentDocumentSnapshot.status.issues);
		})
	);
	public showMoreFrom: boolean = false;
	public showMoreTo: boolean = false;
	public showMoreCc: boolean = false;
	public showMoreBcc: boolean = false;

	public folderName$: Observable<string> = this.docViewerStore.simpleState$.pipe(
		map((data) => data.folder.folderName)
	);
	public audits$ = this.docViewerStore.simpleState$.pipe(
		map((state) => {
			return state.audits;
		})
	);
	public custodianName$: Observable<string> = this.currentDocument$.pipe(
		map((currentDocumentSnapshot) => currentDocumentSnapshot.custodianName));
	public batesFormGroup: FormGroup;
	public batesNumberDigitsOptions = [6, 7, 8, 9, 10];
	public isGlobalEdit: boolean = false;
	public editButtonText: string = 'Edit All';
	public editIconText: string = 'fa-duotone fa-glasses-round';
	private diffDocId: string;
	private emailFieldMap: Map<string, string> = new Map<string, string>([
		['from', 'fromField'],
		['to', 'toField'],
		['cc', 'cc'],
		['bcc', 'bcc'],
	]);
	private localBates: Bates;

	constructor(
		public docViewerStore: DocViewerStore,
		private matDialog: MatDialog,
		private tabStates: TabStates,
		private folderSdk: FolderSdk,
		private notificationManager: NotificationManager,
		private paramReader: DocViewerParamReader,
		private router: Router,
		private route: ActivatedRoute,
		private location: Location,
		private fb: FormBuilder,
		private userConfigsService: UserConfigsService
	) {
		super();

		this.docViewerStore.simpleState$.pipe(
			takeUntil(this.destroy$)
		).subscribe((state) => {
			this.diffDocId = state.compareDocumentId;
			if (state.compareDocumentId) {
				localStorage.setItem('compareDocumentId', this.diffDocId);
			} else {
				localStorage.removeItem('compareDocumentId');
			}
		});

		this.currentDocument$.pipe(
			distinctUntilChanged((a, b) => a.docId === b.docId),
			takeUntil(this.destroy$)
		).subscribe(() => {
			let toggle: boolean = false;
			if (sessionStorage['edit_mode'])
				toggle = sessionStorage['edit_mode'] === 'set';
			this.toggleEdit(toggle);
			this.setBatesForm();
			this.localBates = this.getBates();
		});

		this.docViewerStore.reset$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => {
			this.setBatesForm(true);
		});
	}

	public toggleEdit(toggle: boolean): void {
		if (!this.canEdit())
			toggle = false;
		this.isGlobalEdit = toggle;
		sessionStorage.setItem('edit_mode', toggle ? 'set' : 'unset');
		this.editButtonText = toggle ? 'View Only' : 'Edit All';
		this.editIconText = toggle ? 'fa-duotone fa-glasses-round' : 'fa-duotone fa-pen-to-square';
	}

	public canEdit(): boolean {
		return this.userConfigsService.inRoles(this.roles.EditMetadataFeatureSet);
	}

	public setCompareDoc(): void {
		this.currentDocument$.pipe(
			take(1)
		).subscribe((currentDocument) => {
			this.docViewerStore.updateState((currentState) => {
				currentState.compareDocumentId = currentDocument.docId;
				this.modifyDiffDocIdUrlParam(currentDocument.docId, currentDocument.docId);
				return currentState;
			}).subscribe(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'Compare Doc Set.'
				});
			});
			this.tabStates.showCompareState(true);
		});
	}

	public clearCompareDoc(): void {
		this.docViewerStore.updateState((currentState) => {
			currentState.compareDocumentId = null;
			localStorage.removeItem('compareDocumentId');
			this.tabStates.hideCompareState();
			return currentState;
		}).subscribe(() => {
			this.notificationManager.displaySnackBarMessage({
				message: 'Compare Doc Cleared.'
			});
		});

		this.currentDocument$.pipe(
			take(1)
		).subscribe((currentDocument) => {
			this.modifyDiffDocIdUrlParam(currentDocument.docId, "");
		});
	}

	public setPendingFolderName(folderId: string) {
		this.folderSdk.getFolder$(folderId).subscribe((data) => {
			this.docViewerStore.updateState((state) => {
				state.folder = data;
				return state;
			})
		})
	}

	public showMoreConnections(show: 'to' | 'from' | 'cc' | 'bcc'): void {
		const data = new ConnectionsDialogModel();
		data.type = show;
		const dialogConfig = new MatDialogConfig<ConnectionsDialogModel>();
		dialogConfig.data = data;
		dialogConfig.width = '350px';
		this.matDialog.open(DocViewerConnectionsDialogComponent, dialogConfig);
	}

	public openEmailEdit(type: 'to' | 'from' | 'cc' | 'bcc', emails: string): void {
		if (!this.isGlobalEdit || !this.canEdit())
			return;
		const data = new EmailEditDialogModel();
		data.type = type;
		data.emails = emails ? emails.split(';').filter(email => email.trim() !== '') : [];
		const dialogConfig = new MatDialogConfig<EmailEditDialogModel>();
		dialogConfig.data = data;
		dialogConfig.width = '450px';
		this.matDialog.open(EmailEditComponent, dialogConfig).afterClosed().pipe(
			takeUntil(this.destroy$)
		).subscribe((data) => {
			if (!data)
				return;

			const emails = data.join(';');
			const field = this.emailFieldMap.get(type);
			this.docViewerStore.documentDataFormGroup.controls[field].patchValue(emails);
			this.docViewerStore.documentDataFormGroup.controls[field].markAsDirty();
		});
	}

	public showDocHistoryDialog(): void {
		this.matDialog.open(DocViewerHistoryDialogComponent);
	}

	public folderClick(document: LexbeDoc): void {
		if (!this.isGlobalEdit || !this.canEdit())
			return;
		const data = new MultiDocEditDialogModel();
		data.multiDocGroups = [];
		data.totalDocsSelected = 1;
		data.expandEmailFamilySelected = false;
		data.isDocViewer = true;
		data.dialogInfo = {
			dialogTitle: 'Folder',
			fieldId: BuiltInFieldId.Folder,
			fieldType: FieldType.BuiltInFieldUniqueIdentifier
		};
		const dialogConfig = new MatDialogConfig<MultiDocEditDialogModel>();
		dialogConfig.data = data;
		dialogConfig.width = '400px';
		this.matDialog.open(MultiDocEditComponent, dialogConfig).afterClosed()
			.subscribe((data: MultiDocPostBody) => {
				if (!data) return;
				if (!data.newFieldValue) return;
				document.folderId = data.newFieldValue;
				this.setPendingFolderName(data.newFieldValue);
				this.docViewerStore.documentDataFormGroup.controls['folderId'].patchValue(data.newFieldValue);
				this.docViewerStore.documentDataFormGroup.controls['folderId'].markAsDirty();
			});
	}

	public displayBatesString(): string {
		const bates = this.getBates();
		return this.formatBates(bates.batesPrefix, bates.batesNumberStart, bates.batesNumberDigits);
	}

	public displayConnections(type: 'to' | 'from' | 'cc' | 'bcc'): string[] {
		const field = this.emailFieldMap.get(type);
		const emailString = this.docViewerStore.documentDataFormGroup.get(field).value;
		if (!emailString) {
			this.setEmailShowMore(type, false);
			return [];
		}
		const emailArray = emailString.split(';').filter(email => email.trim() !== '');
		return this.setEmailShowMore(type, emailArray?.length > 2) ? emailArray.slice(0, 2) : emailArray;
	}

	private modifyDiffDocIdUrlParam(currentDocId: string, diffDocId: string): void {
		this.paramReader.read(this.route.snapshot.queryParams);

		if (this.paramReader.diffDocId) {
			this.paramReader.diffDocId = diffDocId;
			const newParams = this.paramReader.changeDocTo(currentDocId)
			const url = this.router.createUrlTree([], {
				relativeTo: this.route,
				queryParams: newParams
			}).toString();
			this.location.replaceState(url);
		}
	}

	private formatBates(prefix: string, numberStart: number, numberDigits: number): string {
		if (prefix !== '' && numberStart)
			return `${prefix} ${numberStart.toString().padStart(numberDigits, '0')}`;

		return '';
	}

	private setBatesForm(isReset: boolean = false): void {
		const bates = isReset ? this.localBates : this.getBates();
		this.batesFormGroup = this.fb.group({
			batesPrefix: [bates.batesPrefix, Validators.required],
			batesNumberStart: [bates.batesNumberStart, [Validators.required, Validators.pattern('^[0-9]*$')]],
			batesNumberDigits: [bates.batesNumberDigits]
		}, { validators: this.batesNumberStartLengthValidator() });

		this.batesFormGroup.get('batesPrefix').valueChanges.pipe(
			takeUntil(this.destroy$)
		).subscribe((v) => {
			sessionStorage['previous_batesPrefix'] = v;
			this.docViewerStore.documentDataFormGroup.controls['batesPrefix'].patchValue(v);
			this.docViewerStore.documentDataFormGroup.controls['batesPrefix'].markAsDirty();
		});

		this.batesFormGroup.get('batesNumberStart').valueChanges.pipe(
			takeUntil(this.destroy$)
		).subscribe((v) => {
			this.docViewerStore.documentDataFormGroup.controls['batesNumberStart'].patchValue(v);
			this.docViewerStore.documentDataFormGroup.controls['batesNumberStart'].markAsDirty();
		});

		this.batesFormGroup.get('batesNumberDigits').valueChanges.pipe(
			takeUntil(this.destroy$)
		).subscribe((v) => {
			sessionStorage['previous_batesNumberDigits'] = v;
			this.docViewerStore.documentDataFormGroup.controls['batesNumberDigits'].patchValue(v);
			this.docViewerStore.documentDataFormGroup.controls['batesNumberDigits'].markAsDirty();
		});
	}

	private getBates(): Bates {
		const batesPrefix = this.docViewerStore.documentDataFormGroup.get('batesPrefix').value || sessionStorage.getItem('previous_batesPrefix') || '';
		const batesNumberStart = this.docViewerStore.documentDataFormGroup.get('batesNumberStart').value !== 0 ? this.docViewerStore.documentDataFormGroup.get('batesNumberStart').value : '';
		const batesNumberDigits = this.getNumberDigits(this.docViewerStore.documentDataFormGroup.get('batesNumberDigits').value);
		return {
			batesPrefix,
			batesNumberStart,
			batesNumberDigits
		};
	}

	private batesNumberStartLengthValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const batesNumberStart = String(control.get('batesNumberStart')?.value);
			const batesNumberDigits = control.get('batesNumberDigits')?.value;
			if (batesNumberStart && batesNumberDigits && batesNumberStart.length > batesNumberDigits) {
				return { 'batesNumberStartLength': true };
			}
			return null;
		};
	}

	private getNumberDigits(numberDigits: number): number {
		if (this.docViewerStore.documentDataFormGroup.get('batesPrefix').value)
			return numberDigits;

		if (sessionStorage['previous_batesNumberDigits'])
			return parseInt(sessionStorage.getItem('previous_batesNumberDigits'));

		return 7;
	}

	private setEmailShowMore(type: 'to' | 'from' | 'cc' | 'bcc', value: boolean): boolean {
		switch (type) {
			case 'to': {
				this.showMoreTo = value;
				return this.showMoreTo;
			}
			case 'from': {
				this.showMoreFrom = value;
				return this.showMoreFrom;
			}
			case 'cc': {
				this.showMoreCc = value;
				return this.showMoreCc;
			}
			case 'bcc': {
				this.showMoreBcc = value;
				return this.showMoreBcc;
			}
			default:
				return false;
		}
	}
}

interface Bates {
	batesPrefix: string;
	batesNumberStart: number;
	batesNumberDigits: number;
}
